/** Local daily totals for dashboard trend charts (no server analytics yet). */

export type DayPoint = {
  date: string; // YYYY-MM-DD (local)
  views: number;
  stars: number;
};

export type InsightsHistory = {
  version: 2;
  days: DayPoint[];
};

const HISTORY_DAYS = 30;

export function historyKey(userId: string) {
  return `robotop.dashboardInsights.history.v2.${userId}`;
}

/** Legacy single-snapshot key from the first insights version. */
function legacySnapshotKey(userId: string) {
  return `robotop.dashboardInsights.${userId}`;
}

export function todayKey(d = new Date()): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseDay(date: string): Date {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function addDays(date: string, n: number): string {
  const d = parseDay(date);
  d.setDate(d.getDate() + n);
  return todayKey(d);
}

export function readHistory(userId: string): DayPoint[] {
  try {
    const raw = localStorage.getItem(historyKey(userId));
    if (raw) {
      const parsed = JSON.parse(raw) as InsightsHistory;
      if (parsed?.version === 2 && Array.isArray(parsed.days)) {
        return parsed.days
          .filter(
            (p) =>
              typeof p?.date === 'string' &&
              typeof p?.views === 'number' &&
              typeof p?.stars === 'number'
          )
          .sort((a, b) => a.date.localeCompare(b.date));
      }
    }

    // Migrate legacy visit snapshot into a one-day history seed
    const legacyRaw = localStorage.getItem(legacySnapshotKey(userId));
    if (legacyRaw) {
      const legacy = JSON.parse(legacyRaw) as { views?: number; stars?: number; at?: number };
      if (typeof legacy.views === 'number' && typeof legacy.stars === 'number') {
        const date =
          typeof legacy.at === 'number' ? todayKey(new Date(legacy.at)) : todayKey();
        return [{ date, views: legacy.views, stars: legacy.stars }];
      }
    }
  } catch {
    /* ignore */
  }
  return [];
}

export function writeHistory(userId: string, days: DayPoint[]) {
  try {
    const trimmed = days
      .slice()
      .sort((a, b) => a.date.localeCompare(b.date))
      .slice(-HISTORY_DAYS);
    const payload: InsightsHistory = { version: 2, days: trimmed };
    localStorage.setItem(historyKey(userId), JSON.stringify(payload));
  } catch {
    /* ignore quota / private mode */
  }
}

/** Upsert today's totals; keep earlier days unchanged. */
export function upsertToday(
  history: DayPoint[],
  totals: { views: number; stars: number }
): DayPoint[] {
  const today = todayKey();
  const withoutToday = history.filter((d) => d.date !== today);
  return [...withoutToday, { date: today, views: totals.views, stars: totals.stars }].sort(
    (a, b) => a.date.localeCompare(b.date)
  );
}

/** Fill calendar gaps by carrying forward the last known totals. */
export function fillDayGaps(days: DayPoint[]): DayPoint[] {
  if (days.length === 0) return [];
  const sorted = [...days].sort((a, b) => a.date.localeCompare(b.date));
  const out: DayPoint[] = [];
  let cursor = sorted[0].date;
  const end = sorted[sorted.length - 1].date;
  const byDate = new Map(sorted.map((d) => [d.date, d]));
  let last = sorted[0];

  while (cursor <= end) {
    const hit = byDate.get(cursor);
    if (hit) last = hit;
    out.push({ date: cursor, views: last.views, stars: last.stars });
    cursor = addDays(cursor, 1);
  }
  return out;
}

/**
 * Chart series for the last N days ending today.
 * Days before the first real sample are drawn as a flat baseline (same totals),
 * so the chart is never empty on the first visit — later days append on top.
 */
export function buildDisplaySeries(
  history: DayPoint[],
  lookbackDays = 7
): DayPoint[] {
  if (history.length === 0) return [];
  const sorted = [...history].sort((a, b) => a.date.localeCompare(b.date));
  const end = todayKey();
  const start = addDays(end, -(Math.max(2, lookbackDays) - 1));
  const byDate = new Map(sorted.map((d) => [d.date, d]));
  const first = sorted[0];

  const out: DayPoint[] = [];
  let last = first;
  let cursor = start;
  while (cursor <= end) {
    if (cursor < first.date) {
      out.push({ date: cursor, views: first.views, stars: first.stars });
    } else {
      const hit = byDate.get(cursor);
      if (hit) last = hit;
      out.push({ date: cursor, views: last.views, stars: last.stars });
    }
    cursor = addDays(cursor, 1);
  }
  return out;
}

/**
 * Percent change from `from` to `to`. Returns null when undefined.
 */
export function percentChange(from: number, to: number): number | null {
  if (from <= 0) return to > 0 ? 100 : null;
  return ((to - from) / from) * 100;
}

export function formatPercent(pct: number | null): string | null {
  if (pct == null || !Number.isFinite(pct) || pct === 0) return null;
  const rounded = Math.round(pct * 10) / 10;
  if (rounded === 0) return null;
  const sign = rounded > 0 ? '+' : '';
  return `${sign}${rounded}%`;
}

export function dailyIncrements(
  series: DayPoint[],
  key: 'views' | 'stars'
): { date: string; value: number }[] {
  if (series.length === 0) return [];
  return series.map((point, i) => ({
    date: point.date,
    value: i === 0 ? 0 : Math.max(0, point[key] - series[i - 1][key]),
  }));
}

export function shortDayLabel(date: string, locale: string): string {
  const d = parseDay(date);
  return d.toLocaleDateString(locale.startsWith('de') ? 'de-DE' : 'en-GB', {
    day: 'numeric',
    month: 'short',
  });
}
